import type { LaneId } from "@fate-kingdom/engine";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";

import {
  ALPHA_CAMERA_ZOOM_DEFAULT,
  ALPHA_CAMERA_PAN_BY_SNAP,
  alphaCameraZoomFromPinch,
  alphaCameraZoomFromWheel,
  alphaCameraMode,
  alphaCameraSnapForPan,
  cameraPointerCentre,
  cameraPointerDistance,
  clampAlphaCameraPanX,
  clampAlphaCameraPanY,
  clampAlphaCameraZoom,
  type AlphaBattlefieldCameraState,
  type AlphaBattlefieldPanSnap,
  type CameraPointerPoint,
} from "./battlefield-camera-types";

interface ManualCameraView {
  readonly x: number;
  readonly y: number;
  readonly zoom: number;
}

interface CameraPanGesture {
  readonly pointerId: number;
  readonly originX: number;
  readonly originY: number;
  readonly originView: ManualCameraView;
  readonly element: HTMLElement;
  captured: boolean;
  latestView: ManualCameraView;
}

interface CameraPinchGesture {
  readonly pointerIds: readonly [number, number];
  readonly initialDistance: number;
  readonly initialCentre: CameraPointerPoint;
  readonly originView: ManualCameraView;
  readonly element: HTMLElement;
  latestView: ManualCameraView;
}

interface UseBattlefieldCameraOptions {
  readonly blocked: boolean;
  readonly result: boolean;
  readonly inspecting: boolean;
  readonly combat: boolean;
  readonly attack: boolean;
  readonly placement: boolean;
  readonly aiTurn: boolean;
  readonly selectedCard: boolean;
  readonly focusLaneId?: LaneId | undefined;
  readonly dragClientY?: number | undefined;
  readonly reducedMotion: boolean;
}

const OVERVIEW_VIEW: ManualCameraView = { x: 0, y: 0, zoom: ALPHA_CAMERA_ZOOM_DEFAULT };

function captureCameraPointer(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // Synthetic browser tests and cancelled native gestures may reject pointer capture.
  }
}

export function useBattlefieldCamera({
  blocked,
  result,
  inspecting,
  combat,
  attack,
  placement,
  aiTurn,
  selectedCard,
  focusLaneId,
  dragClientY,
  reducedMotion,
}: UseBattlefieldCameraOptions) {
  const [snap, setSnapState] = useState<AlphaBattlefieldPanSnap>("overview");
  const [manualView, setManualView] = useState<ManualCameraView>(OVERVIEW_VIEW);
  const [gestureView, setGestureView] = useState<ManualCameraView | null>(null);
  const [zoomGestureActive, setZoomGestureActive] = useState(false);
  const activePointersRef = useRef(new Map<number, CameraPointerPoint>());
  const panGestureRef = useRef<CameraPanGesture | null>(null);
  const pinchGestureRef = useRef<CameraPinchGesture | null>(null);
  const suppressSinglePointerRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const mode = alphaCameraMode({
    result,
    inspecting,
    combat,
    attack,
    placement,
    aiTurn,
    selectedCard,
  });

  const edgeAssist =
    dragClientY === undefined
      ? null
      : dragClientY < window.innerHeight * 0.22
        ? -0.72
        : dragClientY > window.innerHeight * 0.72
          ? 0.58
          : null;
  const automaticPanY =
    mode === "opponent"
      ? -0.62
      : mode === "player"
        ? 0.46
        : mode === "placement"
          ? 0.28
          : mode === "attack" || mode === "combat" || mode === "result"
            ? -0.08
            : manualView.y;
  const currentView = gestureView ?? manualView;
  const panX = clampAlphaCameraPanX(currentView.x);
  const panY = clampAlphaCameraPanY(edgeAssist ?? gestureView?.y ?? automaticPanY);
  const zoom = clampAlphaCameraZoom(currentView.zoom);

  const clearScheduledView = useCallback(() => {
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const scheduleGestureView = useCallback(
    (nextView: ManualCameraView) => {
      clearScheduledView();
      rafRef.current = window.requestAnimationFrame(() => {
        setGestureView(nextView);
        rafRef.current = null;
      });
    },
    [clearScheduledView],
  );

  const releasePanGesture = useCallback(
    (commit: boolean) => {
      const gesture = panGestureRef.current;
      if (gesture?.element.hasPointerCapture(gesture.pointerId)) {
        gesture.element.releasePointerCapture(gesture.pointerId);
      }
      panGestureRef.current = null;
      clearScheduledView();
      setGestureView(null);
      if (!commit || gesture === null) return;
      setManualView(gesture.latestView);
      setSnapState(alphaCameraSnapForPan(gesture.latestView.y));
    },
    [clearScheduledView],
  );

  const releasePinchGesture = useCallback(
    (commit: boolean) => {
      const gesture = pinchGestureRef.current;
      if (gesture !== null) {
        for (const pointerId of gesture.pointerIds) {
          if (gesture.element.hasPointerCapture(pointerId)) {
            gesture.element.releasePointerCapture(pointerId);
          }
        }
      }
      pinchGestureRef.current = null;
      setZoomGestureActive(false);
      clearScheduledView();
      setGestureView(null);
      if (!commit || gesture === null) return;
      setManualView(gesture.latestView);
      setSnapState(alphaCameraSnapForPan(gesture.latestView.y));
    },
    [clearScheduledView],
  );

  const cancelGestures = useCallback(() => {
    releasePanGesture(false);
    releasePinchGesture(false);
    activePointersRef.current.clear();
    suppressSinglePointerRef.current = false;
  }, [releasePanGesture, releasePinchGesture]);

  useEffect(() => {
    if (!blocked) return;
    const timeoutId = window.setTimeout(cancelGestures, 0);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [blocked, cancelGestures]);

  useEffect(
    () => () => {
      clearScheduledView();
    },
    [clearScheduledView],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (
        blocked ||
        event.button !== 0 ||
        (event.target !== event.currentTarget && !(event.target instanceof HTMLCanvasElement))
      ) {
        return;
      }
      activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (activePointersRef.current.size >= 2) {
        releasePanGesture(false);
        const entries = [...activePointersRef.current.entries()].slice(0, 2);
        const first = entries[0];
        const second = entries[1];
        if (first === undefined || second === undefined) return;
        captureCameraPointer(event.currentTarget, first[0]);
        captureCameraPointer(event.currentTarget, second[0]);
        const initialCentre = cameraPointerCentre(first[1], second[1]);
        pinchGestureRef.current = {
          pointerIds: [first[0], second[0]],
          initialDistance: Math.max(1, cameraPointerDistance(first[1], second[1])),
          initialCentre,
          originView: manualView,
          element: event.currentTarget,
          latestView: manualView,
        };
        setZoomGestureActive(true);
        suppressSinglePointerRef.current = true;
        event.preventDefault();
        return;
      }
      if (suppressSinglePointerRef.current) return;
      panGestureRef.current = {
        pointerId: event.pointerId,
        originX: event.clientX,
        originY: event.clientY,
        originView: manualView,
        element: event.currentTarget,
        captured: false,
        latestView: manualView,
      };
    },
    [blocked, manualView, releasePanGesture],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (activePointersRef.current.has(event.pointerId)) {
        activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      }
      const pinch = pinchGestureRef.current;
      if (pinch !== null && pinch.pointerIds.includes(event.pointerId)) {
        const first = activePointersRef.current.get(pinch.pointerIds[0]);
        const second = activePointersRef.current.get(pinch.pointerIds[1]);
        if (first === undefined || second === undefined) return;
        event.preventDefault();
        const centre = cameraPointerCentre(first, second);
        const nextView = {
          x: clampAlphaCameraPanX(
            pinch.originView.x +
              (centre.x - pinch.initialCentre.x) / Math.max(340, window.innerWidth * 1.5),
          ),
          y: clampAlphaCameraPanY(
            pinch.originView.y +
              (centre.y - pinch.initialCentre.y) / Math.max(260, window.innerHeight * 0.55),
          ),
          zoom: alphaCameraZoomFromPinch(
            pinch.originView.zoom,
            pinch.initialDistance,
            cameraPointerDistance(first, second),
          ),
        };
        pinch.latestView = nextView;
        scheduleGestureView(nextView);
        return;
      }

      const gesture = panGestureRef.current;
      if (gesture === null || gesture.pointerId !== event.pointerId) return;
      const dx = event.clientX - gesture.originX;
      const dy = event.clientY - gesture.originY;
      if (!gesture.captured && Math.hypot(dx, dy) < 9) return;
      if (!gesture.captured) {
        captureCameraPointer(gesture.element, event.pointerId);
        gesture.captured = gesture.element.hasPointerCapture(event.pointerId);
      }
      event.preventDefault();
      const nextView = {
        x: clampAlphaCameraPanX(gesture.originView.x + dx / Math.max(340, window.innerWidth * 1.5)),
        y: clampAlphaCameraPanY(
          gesture.originView.y + dy / Math.max(260, window.innerHeight * 0.55),
        ),
        zoom: gesture.originView.zoom,
      };
      gesture.latestView = nextView;
      scheduleGestureView(nextView);
    },
    [scheduleGestureView],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const pinch = pinchGestureRef.current;
      activePointersRef.current.delete(event.pointerId);
      if (pinch !== null && pinch.pointerIds.includes(event.pointerId)) {
        releasePinchGesture(true);
        suppressSinglePointerRef.current = activePointersRef.current.size > 0;
        return;
      }
      if (panGestureRef.current?.pointerId === event.pointerId) releasePanGesture(true);
      if (activePointersRef.current.size === 0) suppressSinglePointerRef.current = false;
    },
    [releasePanGesture, releasePinchGesture],
  );
  const onPointerCancel = useCallback(() => {
    cancelGestures();
  }, [cancelGestures]);

  const setSnap = useCallback((nextSnap: AlphaBattlefieldPanSnap) => {
    setSnapState(nextSnap);
    setManualView((current) => ({
      x: current.x,
      y: ALPHA_CAMERA_PAN_BY_SNAP[nextSnap],
      zoom: current.zoom,
    }));
  }, []);
  const resetOverview = useCallback(() => {
    cancelGestures();
    setSnapState("overview");
    setManualView(OVERVIEW_VIEW);
  }, [cancelGestures]);
  const onWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      if (blocked || activePointersRef.current.size > 0) return;
      event.preventDefault();
      setManualView((current) => ({
        ...current,
        zoom: alphaCameraZoomFromWheel(current.zoom, event.deltaY),
      }));
    },
    [blocked],
  );

  const cameraState = useMemo<AlphaBattlefieldCameraState>(
    () => ({ mode, pan: panY, panX, panY, zoom, snap, focusLaneId }),
    [focusLaneId, mode, panX, panY, snap, zoom],
  );

  return {
    cameraState,
    isPanning: gestureView !== null && !zoomGestureActive,
    isZooming: zoomGestureActive,
    reducedMotion,
    setSnap,
    resetOverview,
    panSurfaceProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onWheel,
    },
  };
}
